import { Controller } from "@hotwired/stimulus"
import { TurboNativeBridge } from "../turbo_native_bridge"

export default class extends Controller {
  static values = { id: Number, promptedUrl: String, cancelledUrl: String, renderedAt: String, trigger: String }

  // Two things mount this controller: a live delivery broadcast, and a native
  // visit within a day of a delivery. Either way the page has to be visible
  // now: a hidden page, a delayed stream or a Turbo snapshot must not turn into
  // an ask.
  connect() {
    if (!this.hasIdValue) return

    this.onBeforeCache = () => this.cancelAsk("before_cache")
    this.onVisibilityChange = () => {
      if (document.visibilityState !== "visible") this.cancelAsk("background")
    }
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    if (this.cancelIfBlocked()) return

    this.askTimeout = setTimeout(() => this.ask(), 2000)
  }

  // Why the ask cannot run right now, or null when it can.
  askBlocker() {
    if (document.visibilityState !== "visible") return "hidden"
    if (document.documentElement.hasAttribute("data-turbo-preview")) return "preview"

    const age = Date.now() - Date.parse(this.renderedAtValue)
    if (!(age >= -5000 && age <= 30000)) return "stale"

    return null
  }

  cancelIfBlocked() {
    const blocker = this.askBlocker()
    if (blocker) this.cancelAsk(blocker)
    return blocker !== null
  }

  // Drops the ask and, when nothing was asked yet, tells the server why: the
  // one signal that separates "nobody was watching" from "the page cancelled".
  cancelAsk(reason) {
    clearTimeout(this.askTimeout)
    this.element.remove()
    if (!this.asked) this.reportCancelled(reason)
  }

  ask() {
    if (this.cancelIfBlocked()) return

    this.asked = true
    // Only the system asks. Without a native side (a browser tab open during an
    // iOS delivery) there is nothing of ours to show.
    if (this.requestNativePrompt()) this.reportPrompted()
    else this.element.remove()
  }

  // Memoised, so one banner asks the system at most once however it is
  // reached. Returns false when there is no native side to ask.
  requestNativePrompt() {
    if (this.nativePromptRequested) return true

    this.nativePromptRequested = TurboNativeBridge.requestAppStoreReview(this.idValue)
    return this.nativePromptRequested
  }



  // Records that the system was asked, and renders the state that follows the
  // ask. The record is what stops a second ask on the next visit: the outcome
  // comes back from the device, so it may never arrive at all.
  async reportPrompted() {
    if (!this.hasPromptedUrlValue) return

    try {
      const response = await fetch(this.promptedUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({ trigger: this.triggerValue })
      })

      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch {
      // The banner stays hidden. The system was already asked, so revealing it
      // to ask again is worse than showing nothing.
    }
  }

  reportCancelled(reason) {
    if (!this.hasCancelledUrlValue || this.cancelReported) return

    this.cancelReported = true
    fetch(this.cancelledUrlValue, {
      method: "POST",
      keepalive: true,
      headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ reason, trigger: this.triggerValue })
    }).catch(() => {})
  }

  get csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  disconnect() {
    clearTimeout(this.askTimeout)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
  }
}
